(function () {
  'use strict'

  const RECENT_STORAGE_KEY = 'skycore:recently-viewed-products'
  const RECENT_LIMIT = 5
  const SHEET_TRANSITION_MS = 600

  function ready (callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback)
    } else {
      callback()
    }
  }

  function getCartRoot (element) {
    return element?.closest?.('[data-ec-cart]') || document.querySelector('[data-ec-cart]')
  }

  function escapeHtml (value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[char])
  }

  function formatMoney (cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents)
    }
    return `$${(Number(cents || 0) / 100).toFixed(2)}`
  }

  function getRootUrl () {
    const root = window.Shopify?.routes?.root || '/'
    return root.endsWith('/') ? root : `${root}/`
  }

  function getProductHandleFromUrl (url) {
    if (!url) return ''

    try {
      const parsed = new URL(url, window.location.origin)
      const parts = parsed.pathname.split('/').filter(Boolean)
      const productIndex = parts.indexOf('products')
      return productIndex >= 0 ? parts[productIndex + 1] || '' : ''
    } catch (error) {
      return ''
    }
  }

  function readRecentHandles () {
    try {
      return JSON.parse(window.localStorage.getItem(RECENT_STORAGE_KEY) || '[]')
        .filter(Boolean)
        .slice(0, RECENT_LIMIT)
    } catch (error) {
      return []
    }
  }

  function writeRecentHandles (handles) {
    try {
      window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(handles.slice(0, RECENT_LIMIT)))
    } catch (error) {
      // Local storage can be unavailable in private contexts.
    }
  }

  function recordRecentlyViewedProduct () {
    const productRoot = document.querySelector('[data-recently-viewed-set][data-product-url]')
    if (!productRoot) return

    const handle = getProductHandleFromUrl(productRoot.dataset.productUrl)
    if (!handle) return

    const nextHandles = [handle, ...readRecentHandles().filter((item) => item !== handle)]
    writeRecentHandles(nextHandles)
  }

  function activateTab (root, tabName) {
    if (!root || !tabName) return

    root.querySelectorAll('[data-ec-cart-tab]').forEach((button) => {
      const active = button.dataset.ecCartTab === tabName
      button.classList.toggle('is-active', active)
      button.setAttribute('aria-selected', active ? 'true' : 'false')
    })

    root.querySelectorAll('[data-ec-cart-panel]').forEach((panel) => {
      const active = panel.dataset.ecCartPanel === tabName
      panel.classList.toggle('is-active', active)
      panel.hidden = !active
    })

    if (tabName === 'recent') {
      loadRecentlyViewed(root)
    }
  }

  function openSheet (root, sheetName) {
    if (!root || !sheetName) return

    root.querySelectorAll('[data-ec-cart-sheet]').forEach((sheet) => {
      const active = sheet.dataset.ecCartSheet === sheetName
      window.clearTimeout(sheet.ecCartSheetTimer)

      if (active) {
        sheet.hidden = false
        window.requestAnimationFrame(() => sheet.classList.add('is-active'))
      } else {
        sheet.classList.remove('is-active')
        sheet.hidden = true
      }
    })

    const activeSheet = root.querySelector(`[data-ec-cart-sheet="${sheetName}"]`)
    const firstField = activeSheet?.querySelector('textarea, input, button')
    window.setTimeout(() => firstField?.focus(), 50)
  }

  function closeSheets (root) {
    if (!root) return

    root.querySelectorAll('[data-ec-cart-sheet]').forEach((sheet) => {
      window.clearTimeout(sheet.ecCartSheetTimer)
      sheet.classList.remove('is-active')
      sheet.ecCartSheetTimer = window.setTimeout(() => {
        if (!sheet.classList.contains('is-active')) {
          sheet.hidden = true
        }
      }, SHEET_TRANSITION_MS)
    })
  }

  function setStatus (element, message, isError = false) {
    if (!element) return

    element.textContent = message
    element.hidden = !message
    element.classList.toggle('is-error', isError)
  }

  async function saveOrderNote (button) {
    const root = getCartRoot(button)
    const input = root?.querySelector('[data-ec-cart-note-input]')
    const status = root?.querySelector('[data-ec-cart-note-status]')
    if (!input || !button) return

    button.setAttribute('aria-busy', 'true')
    setStatus(status, '')

    try {
      const response = await fetch(`${window.Shopify.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: input.value })
      })

      if (!response.ok) throw new Error('Note could not be saved.')

      setStatus(status, 'Order note saved.')
    } catch (error) {
      setStatus(status, error.message || 'Order note could not be saved.', true)
    } finally {
      button.removeAttribute('aria-busy')
    }
  }

  async function applyDiscount (form) {
    const root = getCartRoot(form)
    const input = form.querySelector('[data-ec-cart-discount-input]')
    const status = form.querySelector('[data-ec-cart-discount-status]')
    const submitButton = form.querySelector('[type="submit"]')
    const code = input?.value.trim()

    if (!code) {
      setStatus(status, 'Enter a discount code.', true)
      input?.focus()
      return
    }

    submitButton?.setAttribute('aria-busy', 'true')
    setStatus(status, '')

    try {
      const response = await fetch(`${window.Shopify.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: code,
          sections: ['cart-count-badge', 'offcanvas-cart'],
          sections_url: window.location.pathname
        })
      })
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Discount could not be applied.')
      }

      const invalidCode = data.discount_codes?.find((discount) =>
        String(discount.code).toLowerCase() === code.toLowerCase() && discount.applicable === false
      )

      if (invalidCode) {
        input.value = ''
        setStatus(status, 'Discount code is not valid for this cart.', true)
        input.focus()
        return
      }

      const cartContainer = document.querySelector('cart-container')
      cartContainer?.reloadCartElements?.(data.sections || {})

      window.requestAnimationFrame(() => {
        const nextRoot = document.querySelector('[data-ec-cart]')
        openSheet(nextRoot, 'discount')
        const nextStatus = nextRoot?.querySelector('[data-ec-cart-discount-status]')
        setStatus(nextStatus, 'Discount applied.')
      })
    } catch (error) {
      setStatus(status, error.message || 'Discount could not be applied.', true)
    } finally {
      submitButton?.removeAttribute('aria-busy')
    }
  }

  function productImageUrl (product) {
    const image = product.featured_image || product.images?.[0]
    const src = typeof image === 'string' ? image : image?.src

    if (!src) return ''

    const normalized = src.startsWith('//') ? `${window.location.protocol}${src}` : src

    if (window.Shopify?.resizeImage) {
      return window.Shopify.resizeImage(normalized, '180x180')
    }

    return normalized
  }

  function renderRecentProduct (product) {
    const firstAvailableVariant = product.variants?.find((variant) => variant.available)
    const canAdd = product.variants?.length === 1 && firstAvailableVariant
    const image = productImageUrl(product)
    const productUrl = product.url || `/products/${product.handle}`
    const action = canAdd
      ? `<button class="ec-recent__action" type="button" data-ec-recent-add data-variant-id="${firstAvailableVariant.id}"><span>Add</span></button>`
      : `<a class="ec-recent__action" href="${escapeHtml(productUrl)}"><span>View</span></a>`

    return `
      <article class="ec-recent__item">
        <a class="ec-recent__media" href="${escapeHtml(productUrl)}" tabindex="-1">
          ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
        </a>
        <div>
          <a class="ec-recent__title" href="${escapeHtml(productUrl)}">${escapeHtml(product.title)}</a>
          <p class="ec-recent__price">${formatMoney(product.price)}</p>
        </div>
        ${action}
      </article>
    `
  }

  async function fetchProduct (handle) {
    const response = await fetch(`${getRootUrl()}products/${encodeURIComponent(handle)}.js`)
    if (!response.ok) throw new Error(`Product ${handle} could not be loaded.`)
    return response.json()
  }

  async function loadRecentlyViewed (root) {
    const list = root?.querySelector('[data-ec-recently-viewed-list]')
    const empty = root?.querySelector('[data-ec-recently-viewed-empty]')
    if (!list || list.dataset.loaded === 'true') return

    const handles = readRecentHandles()
    if (!handles.length) {
      empty.hidden = false
      list.innerHTML = ''
      list.dataset.loaded = 'true'
      return
    }

    empty.hidden = true
    list.innerHTML = '<div class="ec-recent__loading">Loading recently viewed products...</div>'

    try {
      const products = (await Promise.allSettled(handles.map(fetchProduct)))
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value)

      if (!products.length) {
        empty.hidden = false
        list.innerHTML = ''
      } else {
        list.innerHTML = products.map(renderRecentProduct).join('')
      }
    } catch (error) {
      empty.hidden = false
      list.innerHTML = ''
    } finally {
      list.dataset.loaded = 'true'
    }
  }

  function handleQuantityButton (button) {
    const wrapper = button.closest('[data-ec-quantity]')
    const input = wrapper?.querySelector('[name="updates[]"]')
    if (!input) return

    const currentValue = Number(input.value || 0)
    const nextValue = button.dataset.ecQuantityButton === 'plus'
      ? currentValue + 1
      : Math.max(currentValue - 1, 0)

    if (nextValue === currentValue) return

    input.value = nextValue
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  function bindEvents () {
    document.addEventListener('click', (event) => {
      const overlay = event.target.closest('.ecoleap-cart-drawer .overlay')
      if (overlay) {
        event.preventDefault()
        const drawer = overlay.closest('.ecoleap-cart-drawer')
        if (drawer && window.bootstrap?.Offcanvas) {
          window.bootstrap.Offcanvas.getOrCreateInstance(drawer).hide()
        }
        return
      }

      const tab = event.target.closest('[data-ec-cart-tab]')
      if (tab) {
        event.preventDefault()
        activateTab(getCartRoot(tab), tab.dataset.ecCartTab)
        return
      }

      const sheetOpen = event.target.closest('[data-ec-cart-sheet-open]')
      if (sheetOpen) {
        event.preventDefault()
        openSheet(getCartRoot(sheetOpen), sheetOpen.dataset.ecCartSheetOpen)
        return
      }

      const sheetClose = event.target.closest('[data-ec-cart-sheet-close]')
      if (sheetClose) {
        event.preventDefault()
        closeSheets(getCartRoot(sheetClose))
        return
      }

      const quantityButton = event.target.closest('[data-ec-quantity-button]')
      if (quantityButton) {
        event.preventDefault()
        handleQuantityButton(quantityButton)
        return
      }

      const noteSave = event.target.closest('[data-ec-cart-note-save]')
      if (noteSave) {
        event.preventDefault()
        saveOrderNote(noteSave)
        return
      }

      const recentAdd = event.target.closest('[data-ec-recent-add]')
      if (recentAdd) {
        event.preventDefault()
        const variantId = Number(recentAdd.dataset.variantId)
        if (variantId) {
          recentAdd.setAttribute('aria-busy', 'true')
          document.querySelector('cart-container')?.add({ items: [{ id: variantId, quantity: 1 }] })
            .finally(() => recentAdd.removeAttribute('aria-busy'))
        }
      }
    })

    document.addEventListener('submit', (event) => {
      const discountForm = event.target.closest('[data-ec-cart-discount-form]')
      if (!discountForm) return

      event.preventDefault()
      applyDiscount(discountForm)
    })

    document.addEventListener('shown.bs.offcanvas', (event) => {
      if (event.target?.id !== 'offcanvas-cart') return

      const overlay = event.target.querySelector('.overlay')
      if (overlay) overlay.setAttribute('aria-expanded', 'true')

      const root = event.target.querySelector('[data-ec-cart]')
      const activeTab = root?.querySelector('[data-ec-cart-tab].is-active')?.dataset.ecCartTab
      if (activeTab === 'recent') {
        loadRecentlyViewed(root)
      }
    })

    document.addEventListener('hidden.bs.offcanvas', (event) => {
      if (event.target?.id !== 'offcanvas-cart') return

      const overlay = event.target.querySelector('.overlay')
      if (overlay) overlay.setAttribute('aria-expanded', 'false')
    })
  }

  ready(() => {
    recordRecentlyViewedProduct()
    bindEvents()
  })
})()
